import * as React from "react";
import moment from "moment-timezone";
import Styles from "./BuildInfo.module.scss";
import { buildInfo } from "src/utils/build-info";

// A small, unobtrusive version chip for the footer. Shows "v <shortSha>" and,
// on click, a popover with the full commit (linked to GitHub), when the UI was
// last built, and the environment. Opens upward since it lives at the bottom of
// the page. Same on mobile and desktop.
export const BuildInfo = () => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Close on outside click / Escape while open.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const { shortSha, commitUrl, buildTime, environment } = buildInfo;
  const updated = buildTime ? moment(buildTime) : null;

  return (
    <div className={Styles.buildInfo} ref={ref}>
      {open && (
        <div className={Styles.popover} role="dialog" aria-label="Build information">
          <div className={Styles.popoverTitle}>Build info</div>
          <dl className={Styles.rows}>
            <div className={Styles.row}>
              <dt>Version</dt>
              <dd>
                {commitUrl ? (
                  <a href={commitUrl} target="_blank" rel="noreferrer noopener">
                    {shortSha} <span aria-hidden="true">↗</span>
                  </a>
                ) : (
                  shortSha
                )}
              </dd>
            </div>
            <div className={Styles.row}>
              <dt>Updated</dt>
              <dd>
                {updated ? (
                  <>
                    {updated.format("MMM D, YYYY h:mm A")}
                    <span className={Styles.relative}>{updated.fromNow()}</span>
                  </>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div className={Styles.row}>
              <dt>Environment</dt>
              <dd className={Styles.env}>{environment.toLowerCase()}</dd>
            </div>
          </dl>
        </div>
      )}
      <button
        type="button"
        className={Styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Build info"
        data-testid="build-info-trigger"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          <path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="7.5" r="1.25" fill="currentColor" />
        </svg>
        <span>v {shortSha}</span>
      </button>
    </div>
  );
};
